import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { verifyCode } from '../../api/passcode.api';
import { codeEffectOf } from '../../domain/codeEffect';
import { startSession } from '../../services/localSession';
import type { OpenedSession } from '../../types/domain.types';

import { errorOf } from './loginFailures';
import type { CodeAsk, LoginCodeSession } from './loginMachine';
import { passcodeMutationKeys } from './mutationKeys';
import { clearSettled } from './useLoginMachine';
import type { LoginMachine } from './useLoginMachine';

interface VerifyInput {
  readonly ask: CodeAsk;
  readonly code: string;
  readonly epoch: number;
  readonly ticket: number;
}

export interface CodeVerifyLogic {
  readonly pending: boolean;
  readonly paused: boolean;
  readonly error: Error | null;
  readonly verify: (session: LoginCodeSession, code?: string) => void;
  readonly clear: () => void;
}

export interface CodeVerifyOptions {
  readonly onOpened: (session: OpenedSession) => void;
}

export const useCodeVerify = (machine: LoginMachine, { onOpened }: CodeVerifyOptions): CodeVerifyLogic => {
  const queryClient = useQueryClient();
  const { dispatch, latest, inFlight } = machine;
  const [answered, setAnswered] = useState<number | null>(null);
  const tickets = useRef(0);
  const verifier = useMutation({
    mutationKey: passcodeMutationKeys.verify(),
    mutationFn: ({ ask, code }: VerifyInput) => verifyCode({ identifier: ask.identifier, code }, undefined),
    onSuccess: (session) => startSession(queryClient, session, 'passcode'),
  });

  const verify = (session: LoginCodeSession, override?: string): void => {
    if (inFlight(passcodeMutationKeys.verify())) return;
    const code = override ?? session.code;
    if (code.length === 0) return;
    tickets.current += 1;
    const input: VerifyInput = { ask: session.target, code, epoch: latest().epoch, ticket: tickets.current };
    verifier.mutate(input, {
      onSuccess: onOpened,
      onError: (failure) => {
        setAnswered(input.ticket);
        const effect = codeEffectOf(failure);
        const at = Date.now();
        if (effect.hold?.kind === 'locked') {
          dispatch({ type: 'locked', epoch: input.epoch, ask: input.ask, seconds: effect.hold.seconds, at });
          return;
        }
        if (effect.hold !== null && effect.hold.reason !== 'LOCKED') {
          dispatch({ type: 'sendHeld', epoch: input.epoch, ask: input.ask, reason: effect.hold.reason, seconds: effect.hold.seconds, at });
        }
        if (effect.newAttempt) dispatch({ type: 'codeRefused' });
      },
    });
  };

  return {
    pending: verifier.isPending,
    paused: verifier.isPaused,
    error: verifier.variables?.ticket === answered ? errorOf(verifier, machine.state.epoch) : null,
    verify,
    clear: () => clearSettled(machine, verifier, passcodeMutationKeys.verify()),
  };
};
